import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStickToBottom } from '../use-stick-to-bottom';
import {
    ArrowUp,
    ChartNoAxesCombined,
    Image as ImageIcon,
    ImagePlus,
    Maximize2,
    Minimize2,
    Package,
    Palette,
    Plus,
    X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ConversationMenu } from './ConversationMenu';
import { MessageBody, ProposalCard, ProposalLike, ReplyMessage } from './ReplyParts';
import { LiveWork, TeamRecap } from '../../agents/components/LiveWork';
import { conversationTimeline } from '../conversation-timeline';
import { AssistantTeamRun } from '../../../../common/hooks/use-assistant-api.hook';
// The studio's prompt bar solved this first; the module is pure, so it is
// imported rather than a second copy of the same caret arithmetic.
import { insertMention, mentionQueryAt } from '../../content-studio/planner/mentions';
import { useAskAi } from '../AskAiContext';
import { SUGGESTIONS, Suggestion, greetingKey } from '../askai-suggestions';
import { DOCKED_WIDTH, DOCK_MIN_VIEWPORT, SHELL_HEIGHT } from '../askai-layout';

/**
 * The panel's own motion, matched to the shell's so the two edges travel
 * together — the shell gives up the space at exactly the rate the panel takes
 * it, and no strip of background shows in between.
 */
const PANEL_MOTION_MS = 200;
const PANEL_MOTION = `${PANEL_MOTION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;

/** How far the expanded panel is held off the window, so the page shows around it. */
const EXPANDED_MARGIN = 16;

const ICONS: Record<Suggestion['kind'], React.ElementType> = {
    analytics: ChartNoAxesCombined,
    content: ImageIcon,
    catalogue: Package,
    design: Palette,
};

/** matchMedia, since the MUI hook left with the rest of it. */
function useMinWidth(px: number): boolean {
    const query = `(min-width:${px}px)`;
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [query]);
    return matches;
}

/**
 * The assistant, in the two sizes it comes in.
 *
 * One component rather than two, because docked and expanded are the same
 * conversation at different widths — splitting them would mean keeping two
 * copies of the composer in step, and the composer is the part that matters.
 */
export const AskAiPanel: React.FC = () => {
    const { t } = useTranslation();
    const askAi = useAskAi();
    const navigate = useNavigate();
    const inputRef = useRef<HTMLTextAreaElement>(null);

    /**
     * What the vendor has changed on each open proposal, by action then by
     * field path.
     *
     * Kept here rather than in the context because it is not part of the
     * conversation: an edit means nothing until Apply sends it, and a vendor
     * who types in a box and then declines has changed nothing. Keyed by
     * action id so two open proposals do not share a draft.
     */
    const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({});

    /** The `@` being typed, if any, and where the caret was when it was. */
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const caretRef = useRef<number>(0);

    const mentionMatches = useMemo(() => {
        if (mentionQuery == null) return [];
        const words = mentionQuery.toLowerCase().split(/\s+/).filter(Boolean);
        const pool = askAi?.mentionable ?? [];
        if (!words.length) return pool.slice(0, 8);
        return pool
            .filter((p) => {
                const hay = `${p.name} ${p.sku ?? ''}`.toLowerCase();
                return words.every((w) => hay.includes(w));
            })
            .slice(0, 8);
    }, [mentionQuery, askAi?.mentionable]);

    /** Put the chosen name in the draft and close the list. */
    const chooseMention = (name: string) => {
        const { value, caret } = insertMention(askAi!.draft, caretRef.current, name);
        askAi!.setDraft(value);
        setMentionQuery(null);
        // The caret belongs after the name just inserted, not where it was.
        window.setTimeout(() => {
            const el = inputRef.current;
            if (el) { el.focus(); el.setSelectionRange(caret, caret); }
        }, 0);
    };
    const fileRef = useRef<HTMLInputElement>(null);

    /*
     * Newest message in view: follows the reply down as its charts and cards
     * render, and always comes down when the vendor sends. It lives here with
     * the scroller it moves.
     */
    const scrollerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const asked = askAi?.messages.filter((m) => m.role === 'vendor').length ?? 0;
    useStickToBottom(scrollerRef, contentRef, `${askAi?.threadUuid ?? ''}:${asked}:${askAi?.size}`);

    const size = askAi?.size ?? 'closed';
    const open = size !== 'closed';
    const expanded = size === 'expanded';
    /**
     * Narrow screens go back to a layer over the page. There is nothing to sit
     * beside on a phone, and a column would leave two unusable ones.
     */
    const docksInline = useMinWidth(DOCK_MIN_VIEWPORT);
    /**
     * Note this does not depend on `open`. A closed panel is still a column —
     * one of zero width. If it only joined the flow on opening, it would snap
     * into place at full width with nothing to animate from; this way the
     * column simply grows, and the page slides across as it does.
     */
    const inline = !expanded && docksInline;

    /** The session list is only worth fetching once the panel is on screen. */
    useEffect(() => {
        if (open) void askAi?.refreshThreads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /** Focus the composer whenever it appears: the vendor opened this to type. */
    useEffect(() => {
        if (!open) return;
        const handle = window.setTimeout(() => inputRef.current?.focus(), 120);
        return () => window.clearTimeout(handle);
    }, [open, expanded]);

    /**
     * The composer grows with the draft (capped near six lines). An effect
     * rather than only the change handler, because suggestions set the draft
     * from outside the textarea.
     */
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 144)}px`;
    }, [askAi?.draft]);

    /** Escape closes, the way every other overlay in the app behaves. */
    useEffect(() => {
        if (!open) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            // Expanded is a place you are working in; step down to docked first
            // rather than throwing the whole conversation off screen.
            if (expanded) askAi?.collapse();
            else askAi?.close();
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, expanded, askAi]);

    // Mounted whether or not it is open. Returning null would mean the panel
    // appears fully formed and vanishes the same way — there is nothing to
    // animate from. Hidden and pushed off the edge instead, and made inert so
    // it cannot be tabbed into while it is not there.
    if (!askAi) return null;

    const greeting = greetingKey();

    /** An image on its own is a question too — "what is this?" */
    const canSend = Boolean(askAi.draft.trim() || askAi.attachments.length);

    /**
     * Which way it slides off.
     *
     * `insetInlineEnd` puts the panel on the correct side in both directions,
     * but transforms have no logical equivalent — translateX(100%) leaves to
     * the right, which is off-screen in English and straight across the page in
     * Arabic. So the sign follows the document direction.
     */
    const offscreen = document.documentElement.dir === 'rtl' ? 'translateX(-100%)' : 'translateX(100%)';

    /**
     * What is attached, before it is sent.
     *
     * Shown as thumbnails rather than filenames: the vendor is about to ask a
     * question about this picture, and "IMG_4821.jpg" does not tell them which
     * picture that is.
     */
    const attachmentStrip = askAi.attachments.length > 0 && (
        <div className="mb-2.5 flex flex-wrap gap-2">
            {askAi.attachments.map((a) => (
                <div key={a.url} className="relative">
                    <img src={a.url} alt={a.name} className="block size-14 rounded-lg border object-cover" />
                    <button
                        type="button"
                        onClick={() => askAi.removeAttachment(a.url)}
                        aria-label={t('ASSISTANT.ATTACH.REMOVE', 'Remove image') as string}
                        className="absolute -top-2 -end-2 flex size-5 items-center justify-center rounded-full border bg-background transition-colors hover:bg-accent"
                    >
                        <X className="size-3" />
                    </button>
                </div>
            ))}
        </div>
    );

    /** Anything image-shaped, from the button, a paste, or a drop. */
    const takeFiles = (files: FileList | null) => {
        for (const file of Array.from(files ?? [])) {
            if (file.type.startsWith('image/')) void askAi.attach(file);
        }
    };

    const composer = (
        <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
                e.preventDefault();
                takeFiles(e.dataTransfer?.files ?? null);
            }}
            className="rounded-xl border bg-background p-3 transition-colors focus-within:border-foreground/40"
        >
            {/*
              * Products the `@` still names.
              *
              * Above the composer rather than below it: the composer sits at the
              * bottom of the panel, and a list under it would open off-screen.
              */}
            {mentionQuery != null && mentionMatches.length > 0 && (
                <div className="mb-2 max-h-[180px] overflow-y-auto rounded-lg border">
                    {mentionMatches.map((p, idx) => (
                        <button
                            key={p.uuid}
                            type="button"
                            onClick={() => chooseMention(p.name)}
                            className={cn(
                                'flex w-full items-baseline gap-2 px-3 py-1.5 text-start transition-colors hover:bg-accent',
                                // The one Enter would take.
                                idx === 0 ? 'bg-accent' : 'bg-background',
                            )}
                        >
                            <span className="min-w-0 flex-1 truncate text-xs">{p.name}</span>
                            {p.sku && <span className="shrink-0 text-[11px] text-muted-foreground">{p.sku}</span>}
                        </button>
                    ))}
                </div>
            )}

            {attachmentStrip}

            <textarea
                ref={inputRef}
                rows={1}
                onPaste={(e) => {
                    // A screenshot in the clipboard is the fastest way to ask
                    // about something on screen, and it arrives as a file.
                    const files = e.clipboardData?.files;
                    if (files?.length) takeFiles(files);
                }}
                value={askAi.draft}
                onChange={(e) => {
                    askAi.setDraft(e.target.value);
                    const caret = e.target.selectionStart ?? e.target.value.length;
                    caretRef.current = caret;
                    setMentionQuery(mentionQueryAt(e.target.value, caret, {
                        accepted: askAi.mentionable.map((p) => p.name),
                        matches: (q) => {
                            const words = q.toLowerCase().split(/\s+/).filter(Boolean);
                            return askAi.mentionable.some((p) => {
                                const hay = `${p.name} ${p.sku ?? ''}`.toLowerCase();
                                return words.every((w) => hay.includes(w));
                            });
                        },
                    }));
                }}
                onKeyDown={(e) => {
                    // While the list is open Enter takes the first match rather
                    // than sending. Sending a half-typed name is the one thing a
                    // vendor cannot undo — the message is already gone.
                    if (e.key === 'Enter' && !e.shiftKey && mentionQuery != null && mentionMatches.length) {
                        e.preventDefault();
                        chooseMention(mentionMatches[0].name);
                        return;
                    }
                    if (e.key === 'Escape' && mentionQuery != null) {
                        e.preventDefault();
                        setMentionQuery(null);
                        return;
                    }
                    // Enter sends, Shift+Enter breaks the line — the convention
                    // every chat box shares, and the one a vendor will try first.
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void askAi.send();
                    }
                }}
                placeholder={
                    t('ASSISTANT.COMPOSER.PLACEHOLDER', 'Ask about your shop, or tell me what to change…') || ''
                }
                className="max-h-36 w-full resize-none border-0 bg-transparent text-sm leading-relaxed outline-none placeholder:text-muted-foreground"
            />

            <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        hidden
                        onChange={(e) => {
                            takeFiles(e.target.files);
                            // Reset, so choosing the same file twice still fires.
                            e.target.value = '';
                        }}
                    />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                disabled={askAi.uploading}
                                onClick={() => fileRef.current?.click()}
                                aria-label={t('ASSISTANT.ATTACH.ADD', 'Attach an image') as string}
                            >
                                <ImagePlus className="size-4.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('ASSISTANT.ATTACH.ADD', 'Attach an image')}</TooltipContent>
                    </Tooltip>

                    <span className="truncate text-xs text-muted-foreground">
                        {askAi.uploading
                            ? t('ASSISTANT.ATTACH.UPLOADING', 'Uploading…')
                            : t('ASSISTANT.COMPOSER.HINT', 'It can read your data and suggest changes.')}
                    </span>
                </div>

                {/*
                  * Disabled until there is something to send — and it stays a
                  * proposal even then: anything that would change the shop is
                  * shown to the vendor before it runs.
                  */}
                <button
                    type="button"
                    onClick={() => void askAi.send()}
                    disabled={(!askAi.draft.trim() && !askAi.attachments.length) || askAi.sending}
                    aria-label={t('ASSISTANT.COMPOSER.SEND', 'Send') || 'Send'}
                    className={cn(
                        'flex size-7 shrink-0 items-center justify-center rounded-full transition-colors',
                        canSend
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                            : 'bg-muted text-muted-foreground',
                    )}
                >
                    <ArrowUp className="size-4.5" />
                </button>
            </div>
        </div>
    );

    const suggestions = (
        <div className="flex flex-col gap-2">
            {SUGGESTIONS.map((s) => {
                const Icon = ICONS[s.kind];

                return (
                    <button
                        key={s.id}
                        type="button"
                        // Asked outright. A suggestion is a question the vendor
                        // picked; filling the box and waiting for Enter makes
                        // them confirm a choice they have already made.
                        onClick={() => void askAi.send(t(s.promptKey, s.promptFallback))}
                        className="flex w-full items-center gap-3 border bg-background p-3 text-start transition-colors hover:border-foreground/40 hover:bg-accent"
                    >
                        <Icon className="size-4.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                            <span className="block text-sm font-medium">{t(s.titleKey, s.titleFallback)}</span>
                            <span className="block text-xs text-muted-foreground">{t(s.hintKey, s.hintFallback)}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );

    /**
     * What each tool is doing, said the way a vendor would say it.
     *
     * Showing `list_open_insights` would be showing them our internals; a
     * name we do not recognise falls back to "working", which is true of any
     * tool we add later and forget to translate.
     */
    const activityLabel = (tool: string | null) =>
        tool
            ? t(`ASSISTANT.ACTIVITY.${tool.toUpperCase()}`, {
                  defaultValue: t('ASSISTANT.ACTIVITY.DEFAULT', 'Working…') as string,
              })
            : t('ASSISTANT.ACTIVITY.DEFAULT', 'Working…');

    // Docked rather than closed: they are going to do something there and
    // may want to come back to what was said about it.
    const goTo = (route: string) => {
        askAi.collapse();
        navigate(route);
    };

    const bubble = (m: ReplyMessage & { team?: AssistantTeamRun[] }, key: string) => (
        <div
            key={key}
            className={cn(
                'max-w-[92%] rounded-xl border px-3.5 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap',
                m.role === 'vendor' ? 'self-end bg-muted' : 'self-start bg-background',
            )}
        >
            {!!m.team?.length && <TeamRecap team={m.team} compact />}
            <MessageBody
                message={m}
                onLink={goTo}
                onAsk={(message) => void askAi.send(message)}
                busy={askAi.sending}
            />
        </div>
    );

    const proposal = (a: ProposalLike) => (
        <ProposalCard
            key={a.actionId}
            action={a}
            edits={edits[a.actionId] ?? {}}
            onEdit={(path, value) =>
                setEdits((prev) => ({ ...prev, [a.actionId]: { ...(prev[a.actionId] ?? {}), [path]: value } }))
            }
            onApply={() => askAi.apply(a.actionId, edits[a.actionId] ?? {})}
            onDecline={() => askAi.decline(a.actionId)}
            handlers={{ onAsk: (message) => void askAi.send(message), onLink: goTo, busy: askAi.sending }}
        />
    );

    /*
     * Read in the order it happened, with one exception.
     *
     * A settled proposal sits where it was made; a pending one sits at the end,
     * because it is the thing waiting for the vendor and the end is where they
     * are looking.
     */
    const { entries, pending } = conversationTimeline(askAi.messages, askAi.actions);

    const conversation = (
        <div data-testid="askai-messages" className="flex flex-col gap-2.5">
            {entries.map((entry) =>
                entry.kind === 'message'
                    ? bubble(entry.message, entry.key)
                    : proposal(entry.action),
            )}
            {pending.map((a) => proposal(a))}

            {askAi.sending && askAi.live && <LiveWork turn={askAi.live} compact />}
            {askAi.sending && !askAi.live && (
                <p className="text-xs text-muted-foreground italic">{activityLabel(askAi.activity)}</p>
            )}

            {askAi.error && <p className="text-xs text-destructive">{askAi.error}</p>}
        </div>
    );

    /**
     * Past conversations. Only in the expanded view: docked, the column is
     * narrow enough that a rail would leave nowhere to read the answer.
     */
    const sessions = (
        // Its own scroll, so a long history never pushes the conversation
        // around, and the two scroll independently.
        <div className="flex w-[260px] shrink-0 flex-col gap-1 overflow-y-auto border-e bg-muted/40 px-3 py-4">
            <button
                type="button"
                onClick={askAi.newChat}
                className="mb-2 flex w-full items-center gap-2 px-2.5 py-2 text-start text-sm font-medium transition-colors hover:bg-accent"
            >
                <Plus className="size-4.5" />
                {t('ASSISTANT.NEW_CHAT', 'New chat')}
            </button>

            <p className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {t('ASSISTANT.SESSIONS', 'Your conversations')}
            </p>

            {askAi.threads.length === 0 && (
                <p className="text-xs text-muted-foreground">{t('ASSISTANT.NO_SESSIONS', 'Nothing yet.')}</p>
            )}

            {askAi.threads.map((thread) => (
                <button
                    key={thread.uuid}
                    type="button"
                    onClick={() => askAi.openThread(thread.uuid)}
                    className={cn(
                        'truncate px-2.5 py-2 text-start text-sm transition-colors hover:bg-accent',
                        thread.uuid === askAi.threadUuid && 'bg-accent',
                    )}
                >
                    {thread.title}
                </button>
            ))}
        </div>
    );

    const header = (
        /*
         * Built exactly like the backoffice's top bar, because it sits beside
         * it and the two dividers have to be one line.
         *
         * TopNavBar is `h-16 border-b`: 64px tall with the rule INSIDE the
         * box, since box-sizing is border-box. This used a 64px header and
         * then a separate 1px element after it, which puts its rule at 64–65
         * — one pixel below the bar it is meant to continue. Close enough to
         * look like a mistake rather than a difference.
         *
         * So: same height, same border, no separate separator.
         */
        <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b px-4 md:px-6">
            <ConversationMenu
                threads={askAi.threads}
                activeUuid={askAi.threadUuid}
                onOpen={askAi.openThread}
                onNew={askAi.newChat}
                onRemove={askAi.removeThread}
            />

            <div className="flex items-center gap-1">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon-sm" onClick={askAi.newChat} aria-label={t('ASSISTANT.NEW_CHAT', 'New chat') as string}>
                            <Plus className="size-4.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('ASSISTANT.NEW_CHAT', 'New chat')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => (expanded ? askAi.collapse() : askAi.expand())}
                            aria-label={(expanded ? t('ASSISTANT.COLLAPSE', 'Collapse') : t('ASSISTANT.EXPAND', 'Expand')) as string}
                        >
                            {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {expanded ? t('ASSISTANT.COLLAPSE', 'Collapse') : t('ASSISTANT.EXPAND', 'Expand')}
                    </TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon-sm" onClick={askAi.close} aria-label={t('ASSISTANT.CLOSE', 'Close') as string}>
                            <X className="size-4.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('ASSISTANT.CLOSE', 'Close')}</TooltipContent>
                </Tooltip>
            </div>
        </div>
    );

    const empty = (
        <div className={cn('flex flex-col items-center justify-center gap-1 text-center', expanded ? 'py-12' : 'py-8')}>
            <p className={cn('font-heading font-medium tracking-tight', expanded ? 'text-3xl' : 'text-xl')}>
                {t(greeting.key, greeting.fallback)}
            </p>
            <p className="text-sm text-muted-foreground">
                {t('ASSISTANT.PROMPT_QUESTION', 'What are we doing today?')}
            </p>
        </div>
    );

    /**
     * Overlaying is the fallback, not the default — the window was too narrow
     * to give the panel a column of its own. A scrim makes that deliberate
     * rather than looking like the panel is simply covering the page, and
     * gives the vendor somewhere to click to dismiss it.
     *
     * Not for expanded, which owns the whole window and has nothing to sit in
     * front of.
     */
    const overlaying = open && !expanded && !inline;

    return (
        <>
            {overlaying && (
                <div
                    onClick={askAi.close}
                    aria-hidden
                    className="fixed inset-0 z-[1299]"
                    style={{
                        // Barely there on purpose. Its job is to catch a click
                        // outside the panel, not to dim the page — the vendor is
                        // asking about what they can see, so darkening it works
                        // against the thing they opened the panel for.
                        backgroundColor: 'color-mix(in oklab, var(--foreground) 4%, transparent)',
                        opacity: open ? 1 : 0,
                        transition: `opacity ${PANEL_MOTION}`,
                    }}
                />
            )}
        <div
            role="dialog"
            aria-label={t('ASSISTANT.TITLE', 'Ask Ardi') || 'Ask Ardi'}
            className={cn(
                'flex max-w-full flex-col bg-background',
                // Clipped, so the header's rule and the composer stop at the
                // corner instead of cutting across it.
                expanded ? 'overflow-hidden rounded-2xl border shadow-lg' : 'border-s',
            )}
            style={{
                /**
                 * Docked, this is a column in the scrolling area — not a layer
                 * over it. That is what keeps the page's scrollbar at the
                 * window's edge instead of stranding it beside the panel.
                 *
                 * Sticky rather than fixed so it holds its place while the page
                 * scrolls past, and still belongs to the flow that reserves its
                 * width.
                 */
                ...(inline
                    ? {
                          position: 'sticky',
                          top: 0,
                          alignSelf: 'flex-start',
                          // The scrollport's height, not the viewport's — a
                          // sticky box taller than what it scrolls in cannot
                          // stick, and drifts with the page instead.
                          height: SHELL_HEIGHT,
                          flexShrink: 0,
                          // Animating width is what moves the page across: the
                          // column grows, the content column gives up the room.
                          width: open ? DOCKED_WIDTH : 0,
                          overflow: 'hidden',
                      }
                    : {
                          position: 'fixed',
                          zIndex: 1300,
                          /*
                           * Expanded is a room, not a takeover.
                           *
                           * Edge to edge it read as a second application that
                           * had replaced the backoffice. Held off the sides it
                           * reads as a layer above the page — the shop still
                           * visible around it, which is what the vendor is
                           * asking about.
                           *
                           * Docked keeps its edges: it is a column against the
                           * window, and a gap there would leave a strip of
                           * page showing down the side of a panel that is
                           * meant to be part of the frame.
                           */
                          top: expanded ? EXPANDED_MARGIN : 0,
                          bottom: expanded ? EXPANDED_MARGIN : 0,
                          // Anchored to the inline end so it lands on the
                          // correct side in Arabic without a second set of rules.
                          insetInlineEnd: expanded ? EXPANDED_MARGIN : 0,
                          insetInlineStart: expanded ? EXPANDED_MARGIN : 'auto',
                          width: expanded ? 'auto' : DOCKED_WIDTH,
                      }),

                // In flow the width animation carries the movement; translating
                // as well would slide it out of a column that is still there.
                transform: inline ? 'none' : open ? 'translateX(0)' : offscreen,
                opacity: open ? 1 : 0,
                // Kept out of the tab order and off the hit-test while closed.
                // `visibility` rather than `display` so the transition has
                // something to run on, and it is delayed on the way out so the
                // slide finishes before the panel stops being painted.
                visibility: open ? 'visible' : 'hidden',
                pointerEvents: open ? 'auto' : 'none',
                transition: [
                    `transform ${PANEL_MOTION}`,
                    `opacity ${PANEL_MOTION}`,
                    `width ${PANEL_MOTION}`,
                    open ? 'visibility 0s' : `visibility 0s linear ${PANEL_MOTION_MS}ms`,
                ].join(', '),
            }}
        >
            {/*
              * Expanded is a two-column room: the history against the leading
              * edge, the conversation filling what is left.
              *
              * The rail sits OUTSIDE the centred column on purpose. Inside it,
              * the two were centred together, which on a wide monitor parked
              * the history somewhere in the middle of the screen with empty
              * space to its left — beside the conversation rather than at the
              * edge of the window.
              *
              * Docked there is no room for it, so the header's own controls
              * carry new-chat and the list is a reason to expand.
              */}
            <div className="flex min-h-0 flex-1">
                {expanded && sessions}

                <div className="flex min-w-0 flex-1 flex-col">
                    {header}

                    <div ref={scrollerRef} className={cn('flex flex-1 justify-center overflow-y-auto py-4', !expanded && 'px-4')}>
                        {/* The conversation keeps a reading width and centres
                            in whatever the rail leaves, rather than stretching
                            across a 2000px monitor. */}
                        <div ref={contentRef} className={cn('w-full', expanded && 'max-w-[720px] px-4')}>
                            {askAi.messages.length === 0 ? (
                                <>
                                    {empty}
                                    <div className="mt-4">{suggestions}</div>
                                </>
                            ) : (
                                conversation
                            )}
                        </div>
                    </div>

                    {/*
                      * The composer keeps its own distance from the last
                      * message.
                      *
                      * The gap used to be whatever padding the scroller
                      * happened to end with, which put the newest reply almost
                      * against the box the vendor types in — the two read as
                      * one block. Owned here, it holds whether the
                      * conversation is short, scrolled, or ends in a table.
                      */}
                    <div className={cn('flex shrink-0 justify-center pt-3 pb-4', !expanded && 'px-4')}>
                        <div className={cn('w-full', expanded && 'max-w-[720px] px-4')}>
                            {composer}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};
